/**
 * @file This file provides a local cache for customer/subscription information. It stores the data in localStorage and if it's out
 * of date, it will fetch the latest. You can use `window.DefaultCustomerInfoObservable.subscribe(callback)` to subscribe to the cached data,
 * including an immediate snapshot of what the data was at time of subscription.
 *
 * You can trigger a refetch by dispatching a custom event indicating that the customer info is expired:
 * - window.dispatchEvent(new Event('customer-info-cache-expired'))
 *
 * NOTE: This file should only be included if there is a currently active customer to avoid unnecessary spam on the back-end.
 *
 * If you need more granular control (including capturing errors), you can listen for the following events:
 *
 * - customer-info-loading: Indicates that loading has begun. The old data may still be used while loading occurs if you've kept a reference to it.
 * - customer-info-loaded: Emitted when customer data is available, which is fired immediately after the dom is loaded in case of a cache hit. Also
 *   fired when the user's active subscription is changed.
 * - customer-info-loading-error: Called if an HTTP error occurred trying to fetch the cache. No further fetches are scheduled automatically.
 */

/**
 * The wrapper for the customer info local cache.
 * @typedef {Object} CustomerInfoLocalCacheWrapper
 * @param {string} lastFetched The last time the cache was updated in ISO8601 format.
 * @param {number} version The value of CURRENT_VERSION at time of cache set. Data cached at lesser versions will be ignored.
 * @param {CustomerInfo} customerInfo The cached customer data.
 */

/**
 * The data for the cached customer info.
 * @typedef {Object} CustomerInfo
 * @param {any} customer The data from the customer endpoint.
 * @param {Array} subscriptions The list of subscriptions from the subscription endpoint.
 * @param {any} activeSubscription If there is a currently active subscription (via localStorage SESSION_SUBSCRIPTION_ID_KEY), that subscription
 *                                 is represented here (pulled from the subscriptions list).
 */

/**
 * Indicates that customer data loading has been triggered. You don't necessarily need to throw out data you already have. Consumers
 * are free to show stale data (indicated by the expired flag on the customer-info-loaded event) while this is loading.
 */
class CustomerInfoLoadingEvent extends Event {
  constructor(expiredData) {
    super('customer-info-loading', { bubbles: true });
    this.expiredData = expiredData;
  }
}

/**
 * An event emitted when customer information is loaded, both from cache and from the back-end.
 * @property {CustomerInfo} data The customer and subscription data. Note that selected* fields should be treated as optional because they
 *                               might be filled in later due to some performance optimizations.
 * @property {number} ageInMS The number of milliseconds accounting for how old the data is.
 * @property {boolean} expired A boolean indicating this data is expired, which, if true, means we've already triggered a refetch. This
 *                             value is useful if you want to show stale data to avoid long load times. Or you can just filter out expired
 *                             data altogether as a consumer. If expired is true, another customer-info-loading event is coming soon.
 */
class CustomerInfoLoadedEvent extends Event {
  constructor(data, ageInMS = 0, expired = false) {
    super('customer-info-loaded', { bubbles: true });

    this.data = {
      ...data,
    };

    this.ageInMS = ageInMS;
    this.expired = expired;
  }

  static fromCacheWrapper(wrapper) {
    return new CustomerInfoLoadedEvent(wrapper.data, wrapper.ageInMS, wrapper.expired);
  }
}

/**
 * An event emitted when partial data is loaded. This allows us to automatically update things in a React app, for instance.
 * @property {string} dataType The type of data that has been loaded.
 * @property {*} data The data loaded, whose shape depends on the type.
 */
class PartialCustomerInfoLoadedEvent extends Event {
  constructor(dataType, data) {
    super('partial-customer-info-loaded', { bubbles: true });

    this.dataType = dataType;
    this.data = data;
  }
}

/**
 * Indicates an error occurred loading customer info. No more fetches are scheduled so if you want to try again, you can raise
 * the customer-info-cache-expired event (or reload the page).
 * @property {Error} error The error raised.
 */
class CustomerInfoLoadingErrorEvent extends Event {
  constructor(error) {
    super('customer-info-loading-error', { bubbles: true });
    this.error = error;
  }
}

/**
 * @typedef CustomerInfoObservableCallback The callback function to execute when customer info has been loaded.
 * @param {*} data The data that is fetched either from the cache or from the server.
 * @param {*} metadata The ageInMS and and expired boolean.
 */

/**
 * A light-weight RxJSish observable-type object that simplifies consumer code by removing the need to call both the initial
 * load and then registering an event handler.
 * Note: Since we're intentionally invoking the callback immediately on subscribe when we have cached data, it is likely that you will
 * receive two identical callbacks very close together, depending on when you registered the subscription.
 * Note: This only handles the happy path (i.e. only the next() of RxJS) and does not offer the option to observe errors or a completion.
 */
class CustomerInfoObservable {
  /**
   * Subscribes to this observable, returning a function that can unsubscribe.
   * @param {CustomerInfoObservableCallback} callback The function to call when an event is raised.
   * @returns {CustomerInfoSubscriber} A subscriber object that allows a caller to unsubscribe.
   */
  subscribe(callback) {
    const current = window.loadCustomerInfoWrapper?.();

    const enrichedCallback = (wrapper) =>
      callback(wrapper.data, { ageInMS: wrapper.ageInMS, expired: wrapper.expired });

    if (current) {
      enrichedCallback(current);
    }

    return new CustomerInfoSubscriber(enrichedCallback);
  }
}

/**
 * Allows for immediate subscription elsewhere by calling `window.DefaultCustomerInfoObservable.subscribe(callback)`
 */
window.DefaultCustomerInfoObservable = new CustomerInfoObservable();

/**
 * A light-weight RxJSish subscriber-type object that lets the caller unsubscribe from an observable.
 */
class CustomerInfoSubscriber {
  constructor(callback) {
    this.callback = callback;

    window.addEventListener('customer-info-loaded', this.callback);
  }

  unsubscribe() {
    window.removeEventListener('customer-info-loaded', this.callback);
  }
}

// Avoid polluting the global namespace.
(() => {
  if (
    !window.SUBSCRIPTIONS_API_BASE_PATH ||
    !window.SESSION_SUBSCRIPTION_ID_KEY ||
    !window.SESSION_CUSTOMER_CACHE_KEY
  ) {
    // If these keys aren't available, it means we're not a logged in customer, so abort.
    return;
  }

  const REFETCH_IN_MS = 24 /*hours*/ * 60 /*minutes*/ * 60 /*seconds*/ * 1000; /*ms*/
  const CURRENT_VERSION = 2;

  const isCurrentlyViewingAccountPage = window.location.pathname === window.SHOPIFY_ROUTES_ACCOUNT_URL;

  /**
   * This holds onto the last scheduled timeout value so we can clear it out if we schedule another. Useful for
   * avoiding overlap caused by external triggers, like changing an active subscription.
   */
  let nextFetchTimeout;

  /**
   * The idea here is that external clients can trigger fetches on this data and if so, we want to make sure the cache
   * contains the latest version of that data. We only update the cache if it exists because if it doesn't, it's highly
   * likely that the whole thing is being fetched and cached right now.
   * We don't update the overall age of the cache because other things are still stale.
   */

  window.tryUpdateExistingCustomerInfoCacheData = (partialFetchResults) => {
    try {
      const raw = localStorage.getItem(SESSION_CUSTOMER_CACHE_KEY);
      if (raw) {
        const existing = safeParseJson(raw);

        if (existing?.data) {
          const newValue = {
            ...existing,
            data: shapeFetchResultsForCaching({
              ...existing.data,
              ...partialFetchResults,
            }),
          };
          localStorage.setItem(SESSION_CUSTOMER_CACHE_KEY, JSON.stringify(newValue));        
        }
      }
    } catch (ex) {
      console.warn('Ignoring failed attempt to update customer cache', ex);
    }
  };

  /**
   * Common API calls for fetching customer and subscription info.
   */
  window.CustomerInfoApi = (() => {
    /**
     * Given optional options for fetch, combines it with the default headers.
     */
    const getFetchOptions = (fetchOptions) => ({
      ...(fetchOptions ?? {}),
      headers: {
        ...(window.SHOPIFY_APP_PROXY_FETCH_DEFAULT_HEADERS ?? {}),
        ...(fetchOptions?.headers ?? {}),
      },
    });

    const HACK_fetch = window.ORIGINAL_FETCH ?? window.fetch;

    return {
      fetchCustomer: async (options) => {
        const resp = await fetch(
          `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/customer`,
          getFetchOptions(options?.fetchOptions),
        );

        if (!resp.ok) {
          return resp;
        }

        const value = await resp.json();

        tryUpdateExistingCustomerInfoCacheData({ customer: value });

        return { ok: true, value };
      },

      fetchSubscriptionSummaries: async (options) => {
        const resp = await HACK_fetch(
          `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/subscriptions/subscriptioninfo?mode=summary`,
          getFetchOptions(options?.fetchOptions),
        );

        if (!resp.ok) {
          return resp;
        }

        const value = (await resp.json())?.subscriptionsData ?? [];

        tryUpdateExistingCustomerInfoCacheData({ subscriptionsSummaries: value });

        if (value) {
          window.dispatchEvent(new PartialCustomerInfoLoadedEvent('subscriptionsSummaries', value));
        }

        return { ok: true, value };
      },

      fetchSubscription: async (subscriptionId, options) => {
        const resp = await HACK_fetch(
          `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/subscriptions/subscriptioninfo?subscriptionId=${encodeURIComponent(subscriptionId)}`,
          getFetchOptions(options?.fetchOptions),
        );

        if (!resp.ok) {
          return resp;
        }

        const value = (await resp?.json())?.subscriptionsData?.[0];

        tryUpdateExistingCustomerInfoCacheData({ selectedSubscription: value });

        if (value) {
          window.dispatchEvent(new PartialCustomerInfoLoadedEvent('subscription', value));
        }

        return { ok: true, value };
      },

      fetchKitData: async (subscriptionId, options) => {
        const resp = await fetch(
          `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/kit-customization/subscription/${encodeURIComponent(subscriptionId)}`,
          getFetchOptions(options?.fetchOptions),
        );

        if (!resp.ok) {
          return resp;
        }

        const value = await resp?.json();

        if (value) {
          window.dispatchEvent(new PartialCustomerInfoLoadedEvent('kitData', value));
        }

        tryUpdateExistingCustomerInfoCacheData({ selectedKitData: value });

        return { ok: true, value };
      },

      /**
       * To be called whenever the subscription selection changes. Errors from here can be ignored.
       */
      persistSelectedSubscriptionId: async (subscriptionId) => {
        try {
          await fetch(
            `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/customer/update/selectedSubscription/${encodeURIComponent(subscriptionId)}`,
            { method: 'PATCH' },
          );
        } catch (ex) {
          console.warn('Could not persist selected subscription on back-end API. Ignoring the error', ex);
        }
      },

      updateExistingSubscription: async (subscriptionId, itemToAdd) => {
        try {
          const payload = {
            operations: [
              {
                operation: 'set-quantity',
                item: {
                  collection_id: itemToAdd.collection_id,
                  collection_source: itemToAdd.collection_source,
                  quantity: itemToAdd.quantity ?? 1,
                  shopify_product_id: itemToAdd.shopify_product_id,
                  shopify_variant_id: itemToAdd.shopify_variant_id,
                },
              },
            ]
          }
          const HACK_fetch = window.ORIGINAL_FETCH ?? window.fetch;
          let response = await HACK_fetch(
            `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/kit-customization/subscription/${encodeURIComponent(subscriptionId)}?include_response=no`,
            {
              method: 'PATCH',
              body: JSON.stringify(payload)
             },
          );
          return response;
        } catch (ex) {
          console.error(ex);
        }
      },
      oneTimePurchase: async (subscriptionId, variantId, quantity) => {
        try {
          let response = await fetch(
            `${window.SUBSCRIPTIONS_API_BASE_PATH}/sap/recharge/onetimes/${encodeURIComponent(subscriptionId)}`,
            {
              method: 'POST',
              body: JSON.stringify({
                variantId,
                quantity: quantity ?? 1,
                request_type: 'one-time',
                add_to_next_charge: true
              })
             },
          );
          return response;
        } catch (ex) {
          console.error(ex);
        }
      },
    };
  })();

  /**
   * Dispatches a loading event, then fetches the latest info from the back-end. If successful, it caches the new data
   * and dispatches a customer-info-loaded event with the new data.
   */
  const fetchCustomerInfo = async () => {
    window.dispatchEvent(new CustomerInfoLoadingEvent());
    const now = new Date();

    try {
      const wrapper = {
        version: CURRENT_VERSION,
        lastFetched: now.toISOString(),
        data: await performFetch(),
      };

      localStorage.setItem(SESSION_CUSTOMER_CACHE_KEY, JSON.stringify(wrapper));
      window.dispatchEvent(new CustomerInfoLoadedEvent(wrapper.data));
      scheduleNextFetch(REFETCH_IN_MS);
    } catch (error) {
      console.error('Error occurred fetching customer data', error);
      window.dispatchEvent(new CustomerInfoLoadingErrorEvent(error));
    }
  };

  /**
   * Clears any previous timeout and schedules a new fetch for the duration specified.
   * @param {number} ms The number of milliseconds to schedule the timeout.
   */
  const scheduleNextFetch = (ms) => {
    if (nextFetchTimeout) {
      window.clearTimeout(nextFetchTimeout);
    }
    nextFetchTimeout = window.setTimeout(fetchCustomerInfo, ms);
  };

  /**
   * Performs the data fetches involved in getting the customer's information.
   *
   * Note on optimization: We intentionally don't trigger the fetch of selected subscription/kit data when
   * we are on the account page because the React SPA fetches the latest every time and will kindly cache
   * that data, thus avoiding duplicate simultaneous API calls. This optimization _does_ rely on the fact
   * that the customer and subscription summaries API endpoints finish before the selected subscription/kit
   * data endpoints. But if the reverse is true it just means they'll be fetched on next page load, so in
   * the worst case we incur an additional fetch on the next page load.
   */
  const performFetch = async () => {
    const subscriptionsSummariesPromise = CustomerInfoApi.fetchSubscriptionSummaries();
    const customerPromise = CustomerInfoApi.fetchCustomer();

    const selectedSubscriptionId = getSelectedSubscriptionId();
    const shouldFetchSelectedData = selectedSubscriptionId && !isCurrentlyViewingAccountPage;

    const selectedSubscriptionPromise = shouldFetchSelectedData
      ? CustomerInfoApi.fetchSubscription(selectedSubscriptionId)
      : Promise.resolve(undefined);

    const selectedKitDataPromise = shouldFetchSelectedData
      ? CustomerInfoApi.fetchKitData(selectedSubscriptionId)
      : Promise.resolve(undefined);

    const [subscriptionsSummariesResult, customerResult, selectedSubscriptionResult, selectedKitDataResult] =
      await Promise.all([
        subscriptionsSummariesPromise,
        customerPromise,
        selectedSubscriptionPromise,
        selectedKitDataPromise,
      ]);

    return await handleAllFetchHttpResults({
      subscriptionsSummariesResult,
      customerResult,
      selectedSubscriptionResult,
      selectedKitDataResult,
    });
  };

  /**
   * All cacheable data has returned. Now we double check that it's ok before caching it and dispatching the final loaded event.
   */
  const handleAllFetchHttpResults = async ({
    subscriptionsSummariesResult,
    customerResult,
    selectedSubscriptionResult,
    selectedKitDataResult,
  }) => {
    if (!subscriptionsSummariesResult.ok) {
      throw new Error('Error fetching subscription summaries info');
    }

    if (!customerResult.ok) {
      throw new Error('Error fetching customer info');
    }

    if (selectedSubscriptionResult != null && !selectedSubscriptionResult.ok) {
      throw new Error('Error fetching selected subscription info');
    }

    if (selectedKitDataResult != null && !selectedKitDataResult.ok) {
      throw new Error('Error fetching kit data');
    }

    return shapeFetchResultsForCaching({
      subscriptionsSummaries: subscriptionsSummariesResult.value,
      customer: customerResult.value,
      selectedSubscription: selectedSubscriptionResult?.value,
      selectedKitData: selectedKitDataResult?.value,
    });
  }; 

  /**
   * This is called when we have the minimal data we need for caching. Here is also where we know enough information about the
   * list of subscriptions for the current user that we can perform an action if they haven't yet selected a subscription, i.e.
   * either automatically selecting a single active subscription or opening the modal for the user to select a subscription.
   */
  const shapeFetchResultsForCaching = ({ subscriptionsSummaries, customer, selectedSubscription, selectedKitData }) => {
    const activeSubscriptionSummaries = subscriptionsSummaries.filter((x) => x.subscription_status === 'active');
    const selectedSubscriptionId = getSelectedSubscriptionId();

    if (!selectedSubscriptionId) {
      if (activeSubscriptionSummaries.length === 1) {
        // This is going to kick off init() again with the new ID.
        persistSelectedSubscriptionId(activeSubscriptionSummaries[0].subscription_id);
      } else if (activeSubscriptionSummaries.length > 1) {
        window.SUBSCRIPTION_MODAL_INIT_OPEN = true;
        window.dispatchEvent(new Event('open-subscription-selection-modal'), { bubbles: true });
      }
    }

    const selectedSubscriptionDataMatchesCurrentId =
      selectedSubscriptionId &&
      selectedSubscription?.subscription_id &&
      Number(selectedSubscription.subscription_id) === selectedSubscriptionId;

    const selectedKitDataMatchesCurrentId =
      selectedSubscriptionId &&
      selectedKitData?.subscriptionId &&
      Number(selectedKitData.subscriptionId) === selectedSubscriptionId;

    return {
      activeSubscriptionCount: activeSubscriptionSummaries.length,
      subscriptionsSummaries,
      customer,
      selectedSubscription: selectedSubscriptionDataMatchesCurrentId ? selectedSubscription : undefined,
      selectedKitData: selectedKitDataMatchesCurrentId ? selectedKitData : undefined,
    };
  };


  /**
   * Loads the fulle JSON from locale storage, containing metadata and the customer data.
   * This is also exposed as window.loadCustomerInfoWrapper() in case you can't subscribe before the
   * first event is fired. This ignores the localStorage if there is a version mismatch.
   */
  const loadCustomerInfoWrapper = () => {
    const wrapperRaw = localStorage.getItem(SESSION_CUSTOMER_CACHE_KEY);
    const wrapper = wrapperRaw ? safeParseJson(wrapperRaw) : undefined;

    if (wrapper?.lastFetched && wrapper?.version === CURRENT_VERSION) {
      const lastFetched = new Date(Date.parse(wrapper.lastFetched));
      const now = new Date();
      const ageInMS = now - lastFetched;

      return {
        ...wrapper,
        ageInMS,
        expired: ageInMS > REFETCH_IN_MS,
      };
    }

    return undefined;
  };

  /** Expose loadCustomerInfoWrapper for those event listeners who are late to the party */
  window.loadCustomerInfoWrapper = loadCustomerInfoWrapper;

  /**
   * This is a safeguard against a situation that might occur due to the optimization that short-circuits selected
   * subscription/kit data when the user is viewing the account page (because the React SPA _always_ loads the latest
   * data and caches it). We check to make sure the cached data has the selected subscription/kit data and if not, we
   * trigger a load of _just_ those sections that are missing (the idea being that the user may have left the account
   * page _prior_ to those two fields getting fetched, thereby leaving the cached entry half-constructed).
   *
   * This will raise a single CustomerInfoLoadedEvent once both the selected subscription and kit data are completely
   * fetched.
   *
   * Don't await this function: It's fire and forget.
   */
  const tryFillingInSelectedDataMissingFromCache = (data) => {
    const selectedSubscriptionId = getSelectedSubscriptionId();

    if (selectedSubscriptionId && !isCurrentlyViewingAccountPage) {
      const notifyIfDataComplete = (resp) => {
        if (resp.ok) {
          const wrapper = loadCustomerInfoWrapper();
          if (wrapper.data?.selectedSubscription && wrapper.data?.selectedKitData) {
            window.dispatchEvent(CustomerInfoLoadedEvent.fromCacheWrapper(wrapper));
          }
        }
      };

      if (selectedSubscriptionId !== Number(data.selectedSubscription?.subscription_id)) {
        CustomerInfoApi.fetchSubscription(selectedSubscriptionId).then(notifyIfDataComplete);
      }

      if (selectedSubscriptionId !== Number(data.selectedKitData?.subscriptionId)) {
        CustomerInfoApi.fetchKitData(selectedSubscriptionId).then(notifyIfDataComplete);
      }
    }
  };

  /**
   * Checks cache and emits loading/loaded events. This can be called multiple times and it will remove any previous refetches.
   */
  const init = async () => {
    const wrapper = loadCustomerInfoWrapper();

    if (wrapper) {
      // We have cached data! Let the world know it is ready to be shown and schedule the next fetch if expired.
      window.dispatchEvent(CustomerInfoLoadedEvent.fromCacheWrapper(wrapper));

      tryFillingInSelectedDataMissingFromCache(wrapper.data);

      if (wrapper.expired === false) {
        // Cache data is not stale so we emit that value and schedule another fetch at the next interval.
        scheduleNextFetch(REFETCH_IN_MS - wrapper.ageInMS);
        return;
      }
    }

    await fetchCustomerInfo();
  };

  window.addEventListener('customer-info-cache-expired', () => {
    // Forcefully remove the old value in case the page reloads before the fetch returns.
    localStorage.removeItem(SESSION_CUSTOMER_CACHE_KEY);
    fetchCustomerInfo();
  });

  /**
   * When the currently active subscription ID (from localStorage) changes, this will clear out the cache,
   * persist the new selection on the back-end, and redirect back to the Account page where all cache entries
   * will begin populating again.
   * NOTE: This is an intentionally disruptive step that was introduced later in the project. We used to reload
   * things on the current page and just let event listeners deal with the changes, but there are other rules
   * about not being able to see certain products which means we'll solve some complexities by just redirecting
   * to the account page and having the user start over.
   */
  window.addEventListener('storage', async (e) => {
    if (e.key === SESSION_SUBSCRIPTION_ID_KEY && e.oldValue !== e.newValue) {
      // We raise this event to tell the Account page not to start fetching latest data when the subscription ID
      // changes. This is admittedly a bit of a hack but it avoids a larger scale refactor.
      window.dispatchEvent(new Event('browser-redirect-imminent'));

      localStorage.removeItem(SESSION_CUSTOMER_CACHE_KEY);

      await CustomerInfoApi.persistSelectedSubscriptionId(e.newValue);

      window.location.href = window.SHOPIFY_ROUTES_ACCOUNT_URL;
    }
  });

  /**
   * When there are multiple subscriptions, show the menu to open the subscription selection modal.
   */


  const onSubscriptionSummariesKnown = (subscriptionsSummaries) => {
    const subscriptionsLinkId = 'multiple-subscriptions-account-link';
    const subscriptionsLink = document.getElementById(subscriptionsLinkId);
    const accountMenu = document.getElementById('HeaderMenu-AccountMenuList');

    const myAccountLink = accountMenu.querySelector('li a[href="/account"]');
    const myAccountMenuEntry = myAccountLink?.parentElement;
    if (myAccountMenuEntry) {     
      myAccountMenuEntry.style.display = subscriptionsSummaries.length ? "block" : "none";
    }
    const showSubscriptionLink =
      subscriptionsSummaries.length > 1 && subscriptionsSummaries.find((x) => x.subscription_status == 'active') || subscriptionsSummaries.length > 1 && subscriptionsSummaries.find((x) => x.subscription_status == 'cancelled' );
    if (subscriptionsLink && !showSubscriptionLink) {
      subscriptionsLink.remove();
    } else if (!subscriptionsLink && showSubscriptionLink && accountMenu && window.SHOPIFY_ROUTES_ACCOUNT_URL) {
      const li = document.createElement('li');
      li.id = subscriptionsLinkId;

      const a = document.createElement('a');

      a.href = window.SHOPIFY_ROUTES_ACCOUNT_URL;
      a.onclick = (e) => {
        e.preventDefault();
        window.dispatchEvent(new Event('open-subscription-selection-modal'), { bubbles: true });
      };

     //const showSubscriptionLinkActive = subscriptionsSummaries.length > 1 && subscriptionsSummaries.find((x) => x.subscription_status == 'active');

      //if (showSubscriptionLinkActive) {
        a.innerText = 'My Subscriptions';
        a.className = accountMenu.querySelector('li:first-child a')?.className; // hijack the style of the first link. Not foolproof
        li.appendChild(a);
  
        accountMenu.prepend(li);
      //}
   
    }
  };

  window.addEventListener('partial-customer-info-loaded', (e) => {
    if (e.dataType === 'subscriptionsSummaries') {
      onSubscriptionSummariesKnown(e.data);
    }
  });

  window.addEventListener('customer-info-loaded', (e) => onSubscriptionSummariesKnown(e.data.subscriptionsSummaries));

  /**
   * Utility function to load JSON and ignore parse errors.
   */
  const safeParseJson = (json) => {
    if (json) {
      try {
        return JSON.parse(json);
      } catch (error) {
        console.warn('Could not parse json from string. Ignoring and returning undefined', json);
      }
    }
    return undefined;
  };

  /**
   * Returns the currently selected subscription ID (based on localStorage) or undefined.
   */
  const getSelectedSubscriptionId = () => {
    const raw = localStorage.getItem(SESSION_SUBSCRIPTION_ID_KEY);
    const numeric = raw ? Number(raw) : undefined;
    return isNaN(numeric) ? undefined : numeric;
  };

  /**
   * Returns the currently selected subscription ID (based on localStorage) or undefined.
   */
  const persistSelectedSubscriptionId = (subscriptionId) => {
    const oldValue = getSelectedSubscriptionId();
    if (oldValue !== subscriptionId) {
      const newValue = `${subscriptionId}`;
      if (subscriptionId) {
        localStorage.setItem(SESSION_SUBSCRIPTION_ID_KEY, newValue);
      } else {
        localStorage.removeItem(SESSION_SUBSCRIPTION_ID_KEY);
      }

      // Storage events are not raised in the current tab so we have to do it ourselves: https://stackoverflow.com/a/72428465
      window.dispatchEvent(
        new StorageEvent('storage', {
          storageArea: localStorage,
          key: SESSION_SUBSCRIPTION_ID_KEY,
          oldValue,
          newValue,
        }),
      );
    }
  };

  document.addEventListener('DOMContentLoaded', init);
})();
